<template>
  <div class="page-container">
    <div class="page-title-container d-flex justify-content-between align-items-center">
      <h1 class="page-title mb-0">Gastos de flota</h1>
      <b-button variant="none" class="button button-primary" :to="{ name: 'fleet-expense-create' }">
        <i class="ri-add-line mr-1"></i> Nuevo gasto
      </b-button>
    </div>

    <!-- ═══════════ CARDS DE RESUMEN ═══════════ -->
    <div class="row mb-3">
      <div class="col-6 col-lg-3 mb-2" v-for="c in cards" :key="c.key">
        <div class="summary-card" :class="c.key">
          <span class="summary-card__label">{{ c.label }}</span>
          <span class="summary-card__value">{{ c.value }}</span>
          <span v-if="c.badge" class="summary-card__badge">{{ c.badge }}</span>
        </div>
      </div>
    </div>

    <div class="table-container">
      <!-- ═══════════ FILTROS ═══════════ -->
      <div class="table-header">
        <div class="w-full d-flex flex-wrap align-items-end">
          <div class="filter-field mr-2 mb-2">
            <label class="filter-label">Desde</label>
            <input type="date" v-model="filters.from" class="toolbar-select" />
          </div>
          <div class="filter-field mr-2 mb-2">
            <label class="filter-label">Hasta</label>
            <input type="date" v-model="filters.to" class="toolbar-select" />
          </div>
          <div class="filter-field mr-2 mb-2">
            <label class="filter-label">Vehículo</label>
            <select v-model="filters.vehicleId" class="toolbar-select">
              <option :value="null">Todos</option>
              <option v-for="v in vehicles" :key="v.id" :value="v.id">{{ v.license_plate }}</option>
            </select>
          </div>
          <div class="filter-field mr-2 mb-2">
            <label class="filter-label">Tipo</label>
            <select v-model="filters.expenseType" class="toolbar-select">
              <option :value="null">Todos</option>
              <option v-for="t in expenseTypes" :key="t.key" :value="t.key">{{ t.label }}</option>
            </select>
          </div>
          <div class="filter-field mr-2 mb-2">
            <label class="filter-label">Método pago</label>
            <select v-model="filters.paymentMethod" class="toolbar-select">
              <option :value="null">Todos</option>
              <option v-for="m in paymentMethods" :key="m.key" :value="m.key">{{ m.label }}</option>
            </select>
          </div>
          <div class="filter-field mr-2 mb-2">
            <label class="filter-label">Estado</label>
            <select v-model="filters.status" class="toolbar-select">
              <option :value="null">Todos</option>
              <option v-for="s in statuses" :key="s.key" :value="s.key">{{ s.label }}</option>
            </select>
          </div>
          <div class="mb-2">
            <b-button variant="none" class="button button-primary mr-2" @click="applyFilters">Filtrar</b-button>
            <b-button variant="none" class="button button-light" @click="clearFilters">Limpiar</b-button>
          </div>
        </div>
      </div>

      <!-- ═══════════ TABLA ═══════════ -->
      <div class="table-responsive mb-0 table-min-height">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th class="pl-3">Fecha</th>
              <th>Vehículo</th>
              <th>Tipo</th>
              <th>Monto</th>
              <th>Método</th>
              <th>Estado</th>
              <th>Fotos</th>
              <th class="pr-md-4">Opciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in list" :key="o.id">
              <td class="text-center pl-3">{{ formatDate(o.expense_date) }}</td>
              <td class="text-center">{{ o.vehicle_plate }}</td>
              <td class="text-center">{{ typeLabel(o.expense_type) }}</td>
              <td class="text-center font-weight-bold">{{ formatMoney(o.amount, o.currency) }}</td>
              <td class="text-center">{{ methodLabel(o.payment_method) }}</td>
              <td class="text-center">
                <span :class="`badge ${statusMeta(o.status).color}`">{{ statusMeta(o.status).label }}</span>
              </td>
              <td class="text-center">
                <button v-if="o.has_invoice" type="button" class="photo-link" title="Ver factura"
                  @click="openPhoto(o, 'invoice')"><i class="ri-file-text-line"></i></button>
                <button v-if="o.has_reimbursement_proof" type="button" class="photo-link" title="Ver comprobante"
                  @click="openPhoto(o, 'reimbursement')"><i class="ri-bank-card-line"></i></button>
                <span v-if="!o.has_invoice && !o.has_reimbursement_proof" class="text-muted">—</span>
              </td>
              <td class="text-center d_option pr-md-3">
                <DropdownTable>
                  <template slot="dropdown-content">
                    <DropdownItem text="Editar"
                      @click="$router.push({ name: 'fleet-expense-edit', params: { id: o.id } })" />
                    <DropdownItem v-if="o.status === 'PENDING'" text="Aprobar" @click="openStatus(o, 'APPROVED')" />
                    <DropdownItem v-if="o.status === 'PENDING'" text="Rechazar" @click="openStatus(o, 'REJECTED')" />
                    <DropdownItem v-if="o.status === 'APPROVED' && o.payment_method === 'CASH'"
                      text="Marcar reembolsado" @click="openStatus(o, 'REIMBURSED')" />
                    <DropdownItem text="Eliminar" @click="openDelete(o)" />
                  </template>
                </DropdownTable>
              </td>
            </tr>
            <tr v-if="!list.length">
              <td colspan="8" class="text-center text-muted py-4">No se encontraron gastos.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer d-flex justify-content-end align-items-center">
        <pagination :total="total" :page="page" :page-size="pageSize" :pages="pages"
          :changePage="changePage" :changePageSize="changePageSize" />
      </div>
    </div>

    <!-- ═══════════ MODAL CAMBIO DE ESTADO ═══════════ -->
    <b-modal v-model="statusModal.show" :title="statusModal.title" hide-footer>
      <p>{{ statusModal.description }}</p>
      <div class="custom-form">
        <div class="form-group">
          <label>Notas (opcional)</label>
          <textarea v-model="statusModal.notes" class="custom-form-control" rows="3"
            placeholder="Observaciones del cambio de estado"></textarea>
        </div>
      </div>
      <div class="text-right">
        <b-button variant="none" class="button button-light mr-2" @click="statusModal.show = false">Cancelar</b-button>
        <b-button variant="none" class="button button-primary" @click="confirmStatus">{{ statusModal.acceptText }}</b-button>
      </div>
    </b-modal>

    <!-- ═══════════ MODAL ELIMINAR ═══════════ -->
    <b-modal v-model="deleteModal.show" title="Eliminar gasto" hide-footer>
      <p>¿Está seguro que desea eliminar este gasto? Esta acción no se puede deshacer.</p>
      <div class="text-right">
        <b-button variant="none" class="button button-light mr-2" @click="deleteModal.show = false">Cancelar</b-button>
        <b-button variant="none" class="button button-primary" @click="confirmDelete">Eliminar</b-button>
      </div>
    </b-modal>

    <!-- ═══════════ LIGHTBOX FOTO ═══════════ -->
    <b-modal v-model="photoModal.show" size="lg" hide-footer :title="photoModal.title">
      <div class="photo-frame text-center">
        <img v-if="photoModal.url" :src="photoModal.url" alt="Foto del gasto" class="photo-img" />
        <p v-else-if="photoModal.error" class="text-muted m-0 py-4">{{ photoModal.error }}</p>
        <p v-else class="text-muted m-0 py-4">Cargando imagen…</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Alert, PaginationMixin, DropdownTable, DropdownItem } from 'wize-admin'
import { FleetExpenseService, VehicleService } from '@/core/services'

const EXPENSE_TYPES = [
  { key: 'FUEL', label: 'Combustible' },
  { key: 'PARKING', label: 'Estacionamiento' },
  { key: 'OTHER', label: 'Otros' }
]
const PAYMENT_METHODS = [
  { key: 'CASH', label: 'Efectivo' },
  { key: 'CARD', label: 'Tarjeta' },
  { key: 'TRANSFER', label: 'Transferencia' },
  { key: 'OTHER', label: 'Otro' }
]
const STATUSES = [
  { key: 'PENDING', label: 'Pendiente', color: 'badge-yellow' },
  { key: 'APPROVED', label: 'Aprobado', color: 'badge-info' },
  { key: 'REIMBURSED', label: 'Reembolsado', color: 'badge-green' },
  { key: 'REJECTED', label: 'Rechazado', color: 'badge-danger' }
]

export default {
  name: 'ExpenseList',
  components: { DropdownTable, DropdownItem },
  mixins: [PaginationMixin],
  data() {
    return {
      list: [],
      vehicles: [],
      summaryData: null,
      expenseTypes: EXPENSE_TYPES,
      paymentMethods: PAYMENT_METHODS,
      statuses: STATUSES,
      filters: {
        from: '', to: '', vehicleId: null,
        expenseType: null, paymentMethod: null, status: null
      },
      statusModal: { show: false, title: '', description: '', acceptText: '', notes: '', target: null, id: null },
      deleteModal: { show: false, id: null },
      photoModal: { show: false, title: '', url: null, error: null }
    }
  },
  computed: {
    // 4 cards: Total + los 3 tipos. km/litro va como badge del card Combustible.
    cards() {
      const byType = (this.summaryData && this.summaryData.by_type) || []
      const totalOf = (t) => {
        const row = byType.find((x) => x.expense_type === t)
        return row ? row.total_amount : 0
      }
      const total = this.summaryData ? this.summaryData.total_amount : 0
      const fuel = this.summaryData && this.summaryData.fuel
      let kmBadge = null
      if (this.filters.vehicleId && fuel && fuel.km_per_liter != null) {
        kmBadge = `${Number(fuel.km_per_liter).toFixed(2)} km/L`
      }
      return [
        { key: 'total', label: 'Total gastado', value: this.formatMoney(total) },
        { key: 'fuel', label: 'Combustible', value: this.formatMoney(totalOf('FUEL')), badge: kmBadge },
        { key: 'parking', label: 'Estacionamiento', value: this.formatMoney(totalOf('PARKING')) },
        { key: 'other', label: 'Otros', value: this.formatMoney(totalOf('OTHER')) }
      ]
    }
  },
  methods: {
    formatMoney(v, currency) {
      const cur = currency || 'PEN'
      const symbol = cur === 'PEN' ? 'S/' : cur + ' '
      return `${symbol} ${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    formatDate(value) {
      if (!value) return '—'
      const parts = String(value).slice(0, 10).split('-')
      if (parts.length !== 3) return value
      return `${parts[2]}/${parts[1]}/${parts[0]}`
    },
    typeLabel(t) { return (EXPENSE_TYPES.find((x) => x.key === t) || {}).label || t },
    methodLabel(m) { return (PAYMENT_METHODS.find((x) => x.key === m) || {}).label || m },
    statusMeta(s) { return STATUSES.find((x) => x.key === s) || { label: s, color: 'badge-secondary' } },

    buildParams() {
      const f = this.filters
      const p = {}
      if (f.from) p.from = f.from
      if (f.to) p.to = f.to
      if (f.vehicleId) p.vehicle_id = f.vehicleId
      if (f.expenseType) p.expense_type = f.expenseType
      if (f.paymentMethod) p.payment_method = f.paymentMethod
      if (f.status) p.status = f.status
      return p
    },
    applyFilters() {
      this.page = 1
      this.loadData()
      this.loadSummary()
    },
    clearFilters() {
      this.filters = { from: '', to: '', vehicleId: null, expenseType: null, paymentMethod: null, status: null }
      this.applyFilters()
    },
    async loadVehicles() {
      try {
        const res = await VehicleService.list({ is_travelcab: true }, { page: 0, size: 1000 })
        const payload = res.payload || {}
        this.vehicles = payload.items || payload || []
      } catch (e) {
        console.error(e)
      }
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const params = { ...this.buildParams(), page: this.page, limit: this.pageSize }
        const res = await FleetExpenseService.list(params)
        this.list = res.payload || []
        this.total = res.metadata ? res.metadata.totalElements : this.list.length
        this.pages = res.metadata ? res.metadata.totalPages : 1
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al listar los gastos')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async loadSummary() {
      try {
        const res = await FleetExpenseService.summary(this.buildParams())
        this.summaryData = res.payload || null
      } catch (e) {
        console.error(e)
      }
    },

    openStatus(o, target) {
      const meta = this.statusMeta(target)
      const map = {
        APPROVED: { title: 'Aprobar gasto', description: '¿Aprobar este gasto?', acceptText: 'Aprobar' },
        REJECTED: { title: 'Rechazar gasto', description: '¿Rechazar este gasto?', acceptText: 'Rechazar' },
        REIMBURSED: { title: 'Marcar reembolsado', description: '¿Marcar este gasto como reembolsado?', acceptText: 'Reembolsar' }
      }
      const cfg = map[target] || { title: 'Cambiar estado', description: '', acceptText: 'Aceptar' }
      this.statusModal = { show: true, target, id: o.id, notes: '', ...cfg }
      // meta se usa solo para consistencia futura de color; no altera la lógica.
      void meta
    },
    async confirmStatus() {
      try {
        await this.$store.dispatch('app/loading', true)
        await FleetExpenseService.updateStatus(this.statusModal.id, {
          status: this.statusModal.target,
          notes: this.statusModal.notes || null
        })
        this.statusModal.show = false
        Alert.success('Estado actualizado correctamente')
        await this.loadData()
        await this.loadSummary()
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo cambiar el estado')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    openDelete(o) {
      this.deleteModal = { show: true, id: o.id }
    },
    async confirmDelete() {
      try {
        await this.$store.dispatch('app/loading', true)
        await FleetExpenseService.remove(this.deleteModal.id)
        this.deleteModal.show = false
        Alert.success('Gasto eliminado correctamente')
        await this.loadData()
        await this.loadSummary()
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo eliminar el gasto')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async openPhoto(o, type) {
      this.photoModal = {
        show: true,
        title: type === 'invoice' ? 'Factura' : 'Comprobante de reembolso',
        url: null,
        error: null
      }
      try {
        const res = await FleetExpenseService.getPhoto(o.id, type)
        this.photoModal.url = res.payload ? res.payload.url : null
        if (!this.photoModal.url) this.photoModal.error = 'No hay foto disponible.'
      } catch (e) {
        console.error(e)
        this.photoModal.error = e.errors ? e.errors.message : 'No se pudo cargar la foto.'
      }
    }
  },
  async mounted() {
    await this.loadVehicles()
    await this.loadData()
    await this.loadSummary()
  }
}
</script>

<style lang="stylus" scoped>
.summary-card
  background #fff
  border 1px solid #e5e7eb
  border-radius 10px
  padding 14px 16px
  display flex
  flex-direction column
  height 100%
  &.total
    border-left 4px solid #2c3e50
  &.fuel
    border-left 4px solid #007bff
  &.parking
    border-left 4px solid #17a2b8
  &.other
    border-left 4px solid #8592A6
.summary-card__label
  font-size 11px
  text-transform uppercase
  letter-spacing .05em
  color #8592A6
.summary-card__value
  font-size 20px
  font-weight 700
  margin-top 4px
.summary-card__badge
  margin-top 6px
  align-self flex-start
  font-size 11px
  font-weight 600
  color #007bff
  background rgba(0,123,255,.1)
  border-radius 6px
  padding 2px 8px

.filter-field
  display flex
  flex-direction column
.filter-label
  font-size 11px
  color #6c757d
  margin-bottom 2px

.photo-link
  background none
  border none
  color #007bff
  font-size 16px
  cursor pointer
  padding 0 4px
  &:hover
    color #0056b3

.photo-frame
  background #f4f5f7
  border 1px solid #e5e7eb
  border-radius 8px
  min-height 220px
  display flex
  align-items center
  justify-content center
  overflow hidden
.photo-img
  max-width 100%
  max-height 70vh
  object-fit contain

.badge-teal
  background-color #20c997
  color #fff
</style>
